import {
  Apple,
  CheckCircle2,
  Download,
  HardDriveDownload,
  MonitorCheck,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { FILE_BASE_URL } from "../../app/apiConfig";

const downloads = [
  {
    key: "mac",
    title: "Mac Tracker",
    subtitle: "For macOS laptops and desktops",
    fileName: "Uptask Tracker.dmg",
    href: `${FILE_BASE_URL}/downloads/Uptask%20Tracker.dmg`,
    icon: Apple,
    accent: "from-cyan-300 to-blue-500",
    badge: "DMG",
  },
  {
    key: "windows",
    title: "Windows Tracker",
    subtitle: "For Windows PCs and laptops",
    fileName: "Uptask Tracker Setup.exe",
    href: `${FILE_BASE_URL}/downloads/Uptask%20Tracker%20Setup.exe`,
    icon: MonitorCheck,
    accent: "from-violet-400 to-fuchsia-500",
    badge: "EXE",
  },
];

const steps = [
  "Download the tracker for your system.",
  "Install and open it once on the employee computer.",
  "Login with the employee Uptask account.",
  "Screenshots will start syncing for Admin and HR review.",
];

export default function TrackerDownloads() {
  return (
    <div className="min-h-screen px-4 py-10 text-white md:px-8 md:py-14">
      <div className="mx-auto max-w-6xl overflow-hidden rounded-[28px] border border-white/12 bg-[#12082b] shadow-[0_30px_90px_rgba(0,0,0,0.42)]">
        <div className="relative overflow-hidden border-b border-white/10 bg-gradient-to-br from-[#213f62] via-[#24114f] to-[#48256c] p-7 sm:p-10">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_15%,rgba(34,211,238,0.28),transparent_28%),radial-gradient(circle_at_88%_5%,rgba(168,85,247,0.25),transparent_30%)]" />
          <div className="relative flex flex-col gap-7 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-cyan-200/30 bg-white/8 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-cyan-100">
                <HardDriveDownload size={16} />
                Tracker Installer
              </div>
              <h1 className="text-4xl font-black leading-tight sm:text-5xl">
                Download Uptask Tracker
              </h1>
              <p className="mt-4 text-base font-medium leading-7 text-white/68 sm:text-lg">
                Install the tracker on employee systems so screenshots are captured and visible to Admin and HR from Uptask.
              </p>
            </div>

            <div className="grid gap-3 rounded-3xl border border-white/12 bg-black/18 p-4 shadow-2xl backdrop-blur-xl sm:grid-cols-3 lg:min-w-[390px]">
              <div className="rounded-2xl bg-white/8 p-4">
                <Users className="mb-3 text-cyan-200" size={22} />
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Access</p>
                <p className="mt-1 text-sm font-black">Admin + HR</p>
              </div>
              <div className="rounded-2xl bg-white/8 p-4">
                <ShieldCheck className="mb-3 text-emerald-200" size={22} />
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Upload</p>
                <p className="mt-1 text-sm font-black">Secure API</p>
              </div>
              <div className="rounded-2xl bg-white/8 p-4">
                <Sparkles className="mb-3 text-yellow-200" size={22} />
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/45">Status</p>
                <p className="mt-1 text-sm font-black">Ready</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-6 md:grid-cols-2 md:p-9">
          {downloads.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                className="group relative overflow-hidden rounded-3xl border border-white/12 bg-white/[0.06] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.28)] transition duration-300 hover:-translate-y-1 hover:border-cyan-200/35 hover:bg-white/[0.09]"
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${item.accent}`} />
                <div className="flex items-start justify-between gap-4">
                  <div className={`grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br ${item.accent} shadow-[0_16px_36px_rgba(34,211,238,0.2)]`}>
                    <Icon size={30} strokeWidth={2.5} />
                  </div>
                  <span className="rounded-full border border-white/14 bg-black/20 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-white/76">
                    {item.badge}
                  </span>
                </div>

                <h2 className="mt-6 text-2xl font-black">{item.title}</h2>
                <p className="mt-2 text-sm font-medium text-white/58">{item.subtitle}</p>
                <p className="mt-5 rounded-2xl border border-white/10 bg-black/16 px-4 py-3 text-sm font-semibold text-cyan-100/80">
                  {item.fileName}
                </p>

                <a
                  href={item.href}
                  download
                  className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-cyan-400 to-violet-500 px-5 py-4 text-sm font-black uppercase tracking-[0.12em] text-white shadow-[0_16px_34px_rgba(59,130,246,0.28)] transition hover:scale-[1.01]"
                >
                  <Download size={18} />
                  Download
                </a>
              </div>
            );
          })}
        </div>

        <div className="border-t border-white/10 bg-black/14 p-6 md:p-9">
          <div className="grid gap-4 md:grid-cols-4">
            {steps.map((step, index) => (
              <div key={step} className="rounded-2xl border border-white/10 bg-white/[0.05] p-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="grid h-8 w-8 place-items-center rounded-xl bg-cyan-300/12 text-sm font-black text-cyan-100">
                    {index + 1}
                  </span>
                  <CheckCircle2 size={18} className="text-emerald-300" />
                </div>
                <p className="text-sm font-semibold leading-6 text-white/72">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
